import { useState, useEffect } from "react";
import {
  CheckBadgeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingLibraryIcon,
  DocumentIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { Menu } from "@headlessui/react";
import StatisticsCards from "../../components/voters/cards/StatisticsCards";
import DemographicsDashboard from "../../components/voters/cards/DemographicsDashboard";
import VotersTable from "../../components/voters/VotersTable";
import AddVoterModal from "../../components/voters/modals/AddVoterModal";
import PrecinctManagementModal from "../../components/voters/modals/PrecinctManagementModal";
import VoterEducationInfoModal from "../../components/voters/modals/VoterEducationInfoModal";
import ElectionPreparationModal from "../../components/voters/modals/ElectionPreparationModal";
import { votersAPI } from "../../services/api";

function VotersPage() {
  const [activeOption, setActiveOption] = useState({
    label: "Precinct Management",
    icon: BuildingLibraryIcon,
  });
  const [showDetails, setShowDetails] = useState(false);
  const toggleDetails = () => setShowDetails((prev) => !prev);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVoterEduModalOpen, setIsVoterEduModalOpen] = useState(false);
  const [isElectionModalOpen, setIsElectionModalOpen] = useState(false);
  const [isPrecinctModalOpen, setIsPrecinctModalOpen] = useState(false);

  const [voters, setVoters] = useState([]);
  const [residents, setResidents] = useState([]);
  const [precincts, setPrecincts] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [votersPerPage, setVotersPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dashboardData, setDashboardData] = useState(null);
  const [ageGroupFilter, setAgeGroupFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [precinctNameFilter, setPrecinctNameFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const menuOptions = [
    { label: "Precinct Management", icon: BuildingLibraryIcon },
    { label: "Voters Education & Info", icon: DocumentIcon },
    { label: "Election Preparation Tools", icon: ListBulletIcon },
  ];

  // Fetch paginated voters
  const fetchVoters = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage.toString(),
        per_page: votersPerPage.toString(),
      };

      // Add age group filter if selected
      if (ageGroupFilter && ageGroupFilter !== "All Age Group") {
        params.age_group = ageGroupFilter;
      }

      // Add search filter if provided
      if (searchTerm && searchTerm.trim() !== "") {
        params.search = searchTerm.trim();
      }

      // Add precinct filter based on selected precinct name (backend expects `precinct` with ID value)
      if (precinctNameFilter && precinctNameFilter !== "All Precinct") {
        // Find the precinct ID from the selected precinct name
        const selectedPrecinct = precincts.find(
          (p) => (p.name || p.precinct_name) === precinctNameFilter
        );
        if (selectedPrecinct) {
          const precinctId = selectedPrecinct.precinct_id ?? selectedPrecinct.id;
          if (precinctId != null) {
            params.precinct = String(precinctId);
          }
        }
      }

      // Add status filter if provided (min 3 characters)
      if (statusFilter && statusFilter !== "All Status" && statusFilter.length >= 3) {
        params.status = statusFilter;
      }

      const res = await votersAPI.getAll(params);

      const apiVoters = res.data?.data?.voters || [];
      const meta = res.data?.data?.meta;

      const mappedVoters = apiVoters.map((v) => {
        const backendGroups = Array.isArray(v.age_group)
          ? v.age_group
          : v.age_group
            ? [v.age_group]
            : [];

        return {
          id: v.voter_id,
          votersIdNo: v.voters_id_no,
          name: v.resident.full_name,
          contact_number: v.resident.contact_number || "N/A",
          registrationDate: v.registration_date,
          precinct: v.precinct?.precinct_name || "N/A",
          address: v.resident.address || "N/A",
          status: v.status === "active" ? "Active" : "Inactive",

          ageGroup: [
            ...backendGroups,
          ],

          precinct_id: v.precinct?.id || null,
          gender: v.resident.gender,
          date_of_birth: v.resident.date_of_birth,
          civil_status: v.resident.civil_status,
          employment_status: v.resident.employment_status,
          education_level: v.resident.education_level,
          purok_zone: v.resident.purok_zone,

          pwd_status: v.resident.pwd_status,
          illiterate_status: v.resident.illiterate_status,
          is_register_voter: v.is_register_voter || 0,

          voter_status: v.status === "active" ? "registered" : "inactive",
        };
      });

      setVoters(mappedVoters);
      setResidents(mappedVoters);
      setTotalPages(meta?.last_page || 1);
      setTotalItems(meta?.total || 0);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch voters.");
      setVoters([]);
      setResidents([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch precincts for filters
  const fetchPrecincts = async () => {
    try {
      const res = await votersAPI.getPrecincts({ per_page: 100 });
      if (res.data?.success) {
        const list = res.data.data.precincts || res.data.data || [];
        setPrecincts(list);
      }
    } catch (err) {
      console.error("Error fetching precincts:", err);
    }
  };

  // Fetch dashboard data
  const fetchDashboard = async () => {
    setDashboardLoading(true);
    try {
      const res = await votersAPI.getDashboard();
      const data = res.data?.data?.voter_dashboard_view || {};
      setDashboardData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    fetchVoters();
  }, [currentPage, votersPerPage, ageGroupFilter, searchTerm, precinctNameFilter, statusFilter, precincts]);

  useEffect(() => {
    fetchDashboard();
    fetchPrecincts();
  }, []);

  // Handlers that also reset page when filters/search change
  const handleSearchChange = (value) => {
    setCurrentPage(1);
    setSearchTerm(value);
  };

  const handleAgeGroupChange = (value) => {
    setCurrentPage(1);
    setAgeGroupFilter(value);
  };

  const handlePrecinctNameChange = (value) => {
    setCurrentPage(1);
    setPrecinctNameFilter(value);
  };

  const handleStatusChange = (value) => {
    setCurrentPage(1);
    setStatusFilter(value);
  };

  return (
    <div className="min-h-screen bg-gray-50 animate-in fade-in duration-1000">
      <div className="max-w-9xl mx-auto p-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-in slide-in-from-top duration-700">
          <div className="flex items-center space-x-4">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg animate-pulse">
              <CheckBadgeIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Voters Management
              </h1>
              <p className="text-gray-600">
                Manage voter registration, precincts, education, and election
                insights
              </p>
            </div>
          </div>

          {/* Right buttons */}
          <div className="flex gap-2">
            <Menu as="div" className="relative inline-block text-left">
              {({ open }) => (
                <>
                 <Menu.Button 
                  className="flex items-center justify-between gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  style={{ backgroundColor: "#DBEAFE", color: "#1E3A8A" }}
                >
                  <div className="flex items-center gap-2">
                    <activeOption.icon className="w-5 h-5" style={{ color: "#2563EB" }} />
                    <span className="font-medium text-sm">{activeOption.label}</span>
                  </div>
                  {open ? (
                    <ChevronUpIcon className="w-4 h-4" style={{ color: "#2563EB" }} />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" style={{ color: "#2563EB" }} />
                  )}
                </Menu.Button>

                  <Menu.Items className="absolute left-0 mt-2 w-64 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-50">
                    <div className="py-1">
                      {menuOptions.map((opt) => (
                        <Menu.Item key={opt.label}>
                          {({ active }) => {
                            const baseClass = `flex items-center gap-2 w-full px-4 py-2 text-sm font-semibold ${active
                              ? "bg-indigo-50 text-indigo-700"
                              : "text-gray-700"
                              }`;

                            const handleClick = () => {
                              setActiveOption({
                                label: opt.label,
                                icon: opt.icon,
                              });

                              if (opt.label === "Precinct Management")
                                setIsPrecinctModalOpen(true);
                              if (opt.label === "Voters Education & Info")
                                setIsVoterEduModalOpen(true);
                              if (opt.label === "Election Preparation Tools")
                                setIsElectionModalOpen(true);
                            };

                            return (
                              <button
                                className={baseClass}
                                onClick={handleClick}
                              >
                                <opt.icon className="w-5 h-5 text-indigo-600" />
                                {opt.label}
                              </button>
                            );
                          }}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </>
              )}
            </Menu>

            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm"
              onClick={() => setIsModalOpen(true)}
            >
              + Resident as Voter
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        {!dashboardLoading && (
          <StatisticsCards dashboardData={dashboardData} />
        )}

        {/* Demographics Dashboard — FIX: removed unused residents/precincts props */}
        {!dashboardLoading && (
          <DemographicsDashboard
            data={dashboardData}
            showDetails={showDetails}
            toggleDetails={toggleDetails}
          />
        )}

        {/* Voters Table */}
        <div className="mb-2">
          <VotersTable
            voters={voters}
            setVoters={setVoters}
            fetchVoters={fetchVoters}
            ageGroupFilter={ageGroupFilter}
            onAgeGroupChange={handleAgeGroupChange}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            precinctNameFilter={precinctNameFilter}
            onPrecinctNameChange={handlePrecinctNameChange}
            statusFilter={statusFilter}
            onStatusChange={handleStatusChange}
            precincts={precincts}
            isLoading={loading}
            pagination={{
              currentPage,
              lastPage: totalPages,
              perPage: votersPerPage,
              total: totalItems,
            }}
            onPageChange={(page, perPage = votersPerPage) => {
              setCurrentPage(page);
              if (perPage !== votersPerPage) {
                setVotersPerPage(perPage);
                setCurrentPage(1);
              }
            }}
          />
        </div>

        {/* Modals */}
        <AddVoterModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onVoterAdded={fetchVoters}
        />
        <PrecinctManagementModal
          isOpen={isPrecinctModalOpen}
          onClose={() => setIsPrecinctModalOpen(false)}
          onPrecinctUpdated={fetchDashboard}
        />
        <VoterEducationInfoModal
          isOpen={isVoterEduModalOpen}
          onClose={() => setIsVoterEduModalOpen(false)}
        />
        <ElectionPreparationModal
          isOpen={isElectionModalOpen}
          onClose={() => setIsElectionModalOpen(false)}
        />

      </div>
    </div>
  );
}

export default VotersPage;